import React, { FC, useMemo, useState } from "react";
import { CheckCircleFill, ExclamationOctagonFill } from "react-bootstrap-icons";
import { useSelector } from "react-redux";
import { selectApplicationDetail } from "../../models/applications/applicationDetailSlice";
import {
  FinancialApplicationWatchlistOrder,
  FinancialApplicationWatchlistReport,
} from "../../models/applications/applicationInterfaces";
import { WatchlistDisplay, WatchlistCategory } from "./watchlistDisplay";

export interface WatchlistScreeningProps {
  watchlistData?: FinancialApplicationWatchlistOrder[];
}

const OFAC_SANCTIONS_DESC =
  "Source Agency: FEDERAL EXCLUSION - Office of Foreign Assets Control";

export const WatchlistScreening: FC<WatchlistScreeningProps> = ({
  watchlistData,
}) => {
  const [activeTab, setActiveTab] = useState<string | undefined>(
    watchlistData?.slice(0, 1)[0]?.id
  );
  const application = useSelector(selectApplicationDetail);
  const permissions = useMemo(
    () => application.application?.permissions,
    [application]
  );

  // Load the report for the active tab.
  const report = useMemo(() => {
    return watchlistData?.find((order) => order.id === activeTab);
  }, [watchlistData, activeTab]);

  // Build display items, splitting OFAC into "OFAC Sanctions" and "Other Watchlist Mentions"
  const displayItems = useMemo(() => {
    if (!report) return [];

    const items: Array<{
      type: WatchlistCategory;
      hits: FinancialApplicationWatchlistReport["hits"];
    }> = [];

    // 1. OFAC Sanctions
    const ofac = report.ofac;
    if (ofac?.hits?.length) {
      const sanctionsHits = ofac.hits.filter(
        (h) => h.description === OFAC_SANCTIONS_DESC
      );
      if (sanctionsHits.length) {
        items.push({ type: "ofac-sanctions", hits: sanctionsHits });
      }
    }

    // 2. PEP
    const pep = report.pep;
    if (pep) {
      items.push({ type: "pep", hits: pep.hits });
    }

    // 3. Negative Media
    const negMedia = report["negative-media"];
    if (negMedia) {
      items.push({ type: "negative-media", hits: negMedia.hits });
    }

    // 4. Other Watchlist Mentions
    if (ofac?.hits?.length) {
      const otherHits = ofac.hits.filter(
        (h) => h.description !== OFAC_SANCTIONS_DESC
      );
      if (otherHits.length) {
        items.push({ type: "ofac-other", hits: otherHits });
      }
    }

    return items;
  }, [report]);

  const hasNoHits = useMemo(() => {
    if (!report) return false;
    const ofac = report.ofac;
    const pep = report.pep;
    const negMedia = report["negative-media"];
    return !ofac?.hits?.length && !pep?.hits?.length && !negMedia?.hits?.length;
  }, [report]);

  const Header = (
    <h1
      className="d-flex text-tertiary h2 mb-4 align-items-center"
      style={{ fontWeight: 750 }}
    >
      <ExclamationOctagonFill className="mr-2" />
      Watchlist Screening Report
    </h1>
  );

  // ------------------------------------------------ RENDER ------------------------------------------------
  if (!permissions?.includes("view-application-watchlist-report")) {
    return (
      <>
        {Header}
        <p>
          You don't have permission to view this Watchlist Screening Report.
        </p>
      </>
    );
  }

  if (!watchlistData || watchlistData.length === 0) {
    return (
      <>
        {Header}
        <p>No watchlist data is available for this application.</p>
      </>
    );
  }

  return (
    <>
      {/* Header */}
      {Header}
      {/* Render the tabs */}
      {watchlistData?.map((order) => {
        return (
          <button
            key={order.id}
            className="bg-transparent mx-2"
            style={{
              borderBottom:
                activeTab === order.id ? "2px solid #010101" : "none",
              borderTop: "none",
              borderLeft: "none",
              borderRight: "none",
              outline: "none",
            }}
            onClick={() => setActiveTab(order.id)}
          >
            <div className="h5 mb-0" style={{ color: "#010101" }}>
              {order.subject}
            </div>
          </button>
        );
      })}
      {/* Render the watchlist data for the active tab */}
      {hasNoHits ? (
        <div
          className="d-flex align-items-center p-3 mt-4 mb-3"
          style={{
            backgroundColor: "#e8f5e9",
            borderRadius: "6px",
            border: "1px solid #c8e6c9",
          }}
        >
          <CheckCircleFill
            className="text-success mr-3"
            style={{ fontSize: "20px" }}
          />
          <span style={{ fontSize: "14px", color: "#2e7d32" }}>
            No watchlist hits detected.
          </span>
        </div>
      ) : (
        <div
          className="p-4 mb-3"
          style={{
            background: "#f6f6f6",
            borderRadius: "8px",
          }}
        >
          {displayItems.map((item) => (
            <WatchlistDisplay
              key={item.type}
              type={item.type}
              hits={item.hits}
            />
          ))}
        </div>
      )}
    </>
  );
};
